import { useNavigate } from 'react-router-dom';
import AdultingSocialLinksCard from '@/components/AdultingSocialLinksCard';
import LegalLinks from '@/components/LegalLinks';
import {
  AppAdultingMarkTile,
  AppButton,
  AppButtonRow,
  AppCard,
  AppCardBody,
  AppCardHeader,
  AppPage,
  AppSectionHero,
  AppSectionHeader,
  AppText,
  LANDING_PAGE_CONTENT,
  PUBLIC_BETA_URLS,
} from '../../ui-foundation';

function openExternal(url: string) {
  window.open(url, '_blank', 'noopener,noreferrer');
}

export default function LandingPage() {
  const navigate = useNavigate();

  return (
    <AppPage className="animate-fade-in">
      <AppSectionHero
        title={LANDING_PAGE_CONTENT.title}
        subtitle={LANDING_PAGE_CONTENT.subtitle}
        titlePrefix={
          <AppText fontSize="$xs" color="rgba(255,255,255,0.72)" fontWeight="700" letterSpacing={1.2} textTransform="uppercase">
            Adulting LifeOS
          </AppText>
        }
        action={
          <AppButtonRow>
            <AppButton size="sm" onPress={() => navigate('/pricing')}>
              Get the beta
            </AppButton>
            <AppButton size="sm" tone="outline" onPress={() => navigate('/about')}>
              Read about Adulting
            </AppButton>
          </AppButtonRow>
        }
      >
        <div className="grid gap-3 md:grid-cols-3">
          <div className="rounded-2xl border border-white/15 bg-white/10 p-4">
            <p className="text-xs font-semibold uppercase tracking-[0.14em] text-white/70">Founder-led</p>
            <p className="mt-2 text-sm font-semibold text-white">Built by Hannah Graham for the real admin load.</p>
          </div>
          <div className="rounded-2xl border border-white/15 bg-white/10 p-4">
            <p className="text-xs font-semibold uppercase tracking-[0.14em] text-white/70">UK-first</p>
            <p className="mt-2 text-sm font-semibold text-white">Benefits, NHS, school, tenancy, and HMRC realities built in.</p>
          </div>
          <div className="rounded-2xl border border-white/15 bg-white/10 p-4">
            <p className="text-xs font-semibold uppercase tracking-[0.14em] text-white/70">Calm by design</p>
            <p className="mt-2 text-sm font-semibold text-white">One place to remember what matters before it turns into stress.</p>
          </div>
        </div>
      </AppSectionHero>

      <div className="grid gap-4 lg:grid-cols-2">
        <AppCard>
          <AppCardHeader>
            <AppSectionHeader
              title={LANDING_PAGE_CONTENT.forEveryoneTitle}
              subtitle="Founder-led by Hannah Graham and operated by Steady House Ltd."
            />
          </AppCardHeader>
          <AppCardBody>
            <p className="text-sm leading-relaxed text-muted-foreground">
              {LANDING_PAGE_CONTENT.forEveryoneBody}
            </p>
            <AppButtonRow>
              <AppButton size="sm" tone="outline" onPress={() => navigate('/about')}>
                About Adulting
              </AppButton>
            </AppButtonRow>
          </AppCardBody>
        </AppCard>

        <AppCard>
          <AppCardHeader>
            <AppSectionHeader
              title={LANDING_PAGE_CONTENT.trustTitle}
              subtitle="A few reasons Adulting feels steadier than a generic life-admin app."
            />
          </AppCardHeader>
          <AppCardBody>
            <div className="grid gap-3 sm:grid-cols-2">
              {LANDING_PAGE_CONTENT.trust.map((item) => (
                <div key={item.title} className="rounded-2xl border border-border bg-background px-4 py-3">
                  <p className="text-sm font-semibold text-foreground">
                    <span className="mr-2 text-base">{item.emoji}</span>
                    {item.title}
                  </p>
                  <p className="mt-1 text-xs leading-relaxed text-muted-foreground">{item.body}</p>
                </div>
              ))}
            </div>
          </AppCardBody>
        </AppCard>
      </div>

      <AppCard>
        <AppCardHeader>
          <AppSectionHeader
            title={LANDING_PAGE_CONTENT.installTitle}
            subtitle={LANDING_PAGE_CONTENT.installSubtitle}
          />
        </AppCardHeader>
        <AppCardBody>
          <p className="text-sm leading-relaxed text-muted-foreground">
            {LANDING_PAGE_CONTENT.installBody}
          </p>

          <div className="grid gap-4 lg:grid-cols-[1.1fr,0.9fr]">
            <AppCard backgroundColor="$background" px="$sm" py="$sm">
              <AppCardHeader>
                <AppSectionHeader
                  title={LANDING_PAGE_CONTENT.chooseDeviceTitle}
                  subtitle={LANDING_PAGE_CONTENT.chooseDeviceSubtitle}
                />
              </AppCardHeader>
              <AppCardBody>
                <AppButtonRow flexDirection="column" alignItems="stretch">
                  <AppButton size="sm" tone="outline" onPress={() => openExternal(PUBLIC_BETA_URLS.testflightDownload)}>
                    Download TestFlight
                  </AppButton>
                  <AppButton size="sm" onPress={() => openExternal(PUBLIC_BETA_URLS.iphoneBeta)}>
                    Join the Adulting iPhone beta
                  </AppButton>
                  <AppButton size="sm" tone="outline" onPress={() => openExternal(PUBLIC_BETA_URLS.androidBeta)}>
                    Join the Adulting Android beta
                  </AppButton>
                </AppButtonRow>
              </AppCardBody>
            </AppCard>

            <AppCard backgroundColor="$background" px="$sm" py="$sm">
              <AppCardHeader>
                <AppSectionHeader
                  title={LANDING_PAGE_CONTENT.androidTitle}
                  subtitle={LANDING_PAGE_CONTENT.androidSubtitle}
                />
              </AppCardHeader>
              <AppCardBody>
                <ol className="list-decimal space-y-3 pl-5 text-sm leading-relaxed text-muted-foreground">
                  {LANDING_PAGE_CONTENT.androidSteps.map((step) => (
                    <li key={step}>{step}</li>
                  ))}
                </ol>
                <p className="text-sm leading-relaxed text-muted-foreground">
                  {LANDING_PAGE_CONTENT.androidBody}
                </p>
              </AppCardBody>
            </AppCard>
          </div>
        </AppCardBody>
      </AppCard>

      <AppCard>
        <AppCardHeader>
          <AppSectionHeader
            title={LANDING_PAGE_CONTENT.howItWorksTitle}
            subtitle={LANDING_PAGE_CONTENT.howItWorksSubtitle}
          />
        </AppCardHeader>
        <AppCardBody>
          <div className="grid gap-4 md:grid-cols-3">
            {LANDING_PAGE_CONTENT.howItWorks.map((item) => (
              <AppCard key={item.step} backgroundColor="$background" px="$sm" py="$sm">
                <AppCardBody>
                  <p className="text-xs font-semibold uppercase tracking-[0.12em] text-primary">{item.step}</p>
                  <h3 className="text-base font-semibold text-foreground">{item.title}</h3>
                  <p className="text-sm leading-relaxed text-muted-foreground">{item.body}</p>
                </AppCardBody>
              </AppCard>
            ))}
          </div>
        </AppCardBody>
      </AppCard>

      <AppCard>
        <AppCardHeader>
          <AppSectionHeader title={LANDING_PAGE_CONTENT.everythingTitle} />
        </AppCardHeader>
        <AppCardBody>
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
            {LANDING_PAGE_CONTENT.features.map((feature) => (
              <AppCard key={feature.title} backgroundColor="$background" px="$sm" py="$sm">
                <AppCardBody>
                  <span className="text-2xl">{feature.emoji}</span>
                  <h3 className="text-sm font-semibold text-foreground">{feature.title}</h3>
                  <p className="text-xs leading-relaxed text-muted-foreground">{feature.body}</p>
                </AppCardBody>
              </AppCard>
            ))}
          </div>
        </AppCardBody>
      </AppCard>

      <AppCard>
        <AppCardHeader>
          <AppSectionHeader
            title={LANDING_PAGE_CONTENT.benefitsTitle}
            subtitle={LANDING_PAGE_CONTENT.benefitsSubtitle}
          />
        </AppCardHeader>
        <AppCardBody>
          <div className="grid gap-4 md:grid-cols-2">
            {LANDING_PAGE_CONTENT.benefits.map((item) => (
              <AppCard key={item.title} backgroundColor="$background" px="$sm" py="$sm">
                <AppCardBody>
                  <h3 className="text-base font-semibold text-foreground">{item.title}</h3>
                  <p className="text-sm leading-relaxed text-muted-foreground">{item.body}</p>
                </AppCardBody>
              </AppCard>
            ))}
          </div>
        </AppCardBody>
      </AppCard>

      <AdultingSocialLinksCard
        sourceScreen="public_landing"
        helperText={LANDING_PAGE_CONTENT.socialHelper}
      />

      <AppCard>
        <AppCardHeader>
          <AppSectionHeader title={LANDING_PAGE_CONTENT.nextTitle} subtitle={LANDING_PAGE_CONTENT.nextSubtitle} />
        </AppCardHeader>
        <AppCardBody>
          <AppButtonRow>
            <AppButton size="sm" onPress={() => navigate('/about')}>
              About Adulting
            </AppButton>
            <AppButton size="sm" tone="outline" onPress={() => openExternal(PUBLIC_BETA_URLS.steadyHouseWebsite)}>
              Visit Steady House
            </AppButton>
            <AppButton size="sm" tone="outline" onPress={() => openExternal(PUBLIC_BETA_URLS.kindredWebsite)}>
              Explore Kindred
            </AppButton>
          </AppButtonRow>

          <div className="flex items-center gap-3 rounded-2xl border border-border bg-background px-4 py-3">
            <AppAdultingMarkTile tileSize={36} imageWidth={22} imageHeight={19} />
            <div className="space-y-0.5">
              <p className="text-sm font-semibold text-foreground">Adulting is a founder-led Steady House product.</p>
              <span className="block text-xs text-muted-foreground">Life admin for real life, with Kindred as the sister app for single-parent community and support.</span>
            </div>
          </div>
        </AppCardBody>
      </AppCard>

      <AppCard>
        <AppCardHeader>
          <AppSectionHeader
            title="Already using Adulting?"
            subtitle="Log back in here, or use the beta links above if you want the app on your phone."
          />
        </AppCardHeader>
        <AppCardBody>
          <p className="text-sm leading-relaxed text-muted-foreground">
            If Adulting opens on the public landing page from your saved web app, use this sign-in route to get straight back into your account.
          </p>
          <AppButtonRow>
            <AppButton size="sm" onPress={() => navigate('/auth')}>
              Log in
            </AppButton>
            <AppButton size="sm" tone="outline" onPress={() => navigate('/pricing')}>
              Beta details
            </AppButton>
          </AppButtonRow>
          <p className="text-xs leading-relaxed text-muted-foreground">
            If you have turned on two-step verification, Adulting will ask for your authenticator code after password sign-in.
          </p>
        </AppCardBody>
      </AppCard>

      <div className="pt-2">
        <LegalLinks tone="public" />
      </div>
    </AppPage>
  );
}
